import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Copy, ExternalLink, Check, Info } from 'lucide-react';
import { construirLinkWaMe, PLANTILLA_CANONICA_ES_MX } from '@shared/parsePedidoWa';
import { useNegocioStore } from '@renderer/store/useNegocioStore';

/**
 * Sprint 35 — Builder de plantilla wa.me para que la dueña envíe a sus
 * clientes un link que abre WhatsApp con el formato listo para rellenar.
 *
 * UX:
 *  - Teléfono del negocio precargado del Negocio (editable acá si no estaba seteado).
 *  - Plantilla editable (default = PLANTILLA_CANONICA_ES_MX).
 *  - Link generado en tiempo real.
 *  - Botones: Copiar link, Probar (abre WhatsApp Web).
 *  - Instrucciones de uso para la dueña no-técnica.
 */
export function PlantillaWaForm() {
  const negocio = useNegocioStore((s) => s.negocio);
  const cargarNegocio = useNegocioStore((s) => s.cargar);
  const [telefono, setTelefono] = useState(negocio?.telefono ?? '');
  /**
   * Sprint 98 — La plantilla ahora se guarda.
   *
   * Desde el Sprint 35 esta pantalla decía *"edítala si quieres"* y la edición **no se persistía en
   * ningún lado**: vivía aquí, en el estado del componente, y al salir de Configuración volvía al
   * texto original. Sesenta y tres sprints prometiendo algo que no ocurría.
   *
   * Ahora vive en `Negocio.plantillaPedidoWa`; `null` significa "usa la canónica del código".
   */
  const [plantilla, setPlantilla] = useState(negocio?.plantillaPedidoWa || PLANTILLA_CANONICA_ES_MX);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [copiadoTexto, setCopiadoTexto] = useState(false);

  useEffect(() => {
    if (negocio?.telefono && !telefono) {
      setTelefono(negocio.telefono);
    }
  }, [negocio?.telefono]);

  // Cuando el negocio termina de cargar, se adopta lo que la dueña haya guardado.
  useEffect(() => {
    if (negocio?.plantillaPedidoWa) setPlantilla(negocio.plantillaPedidoWa);
  }, [negocio?.plantillaPedidoWa]);

  const sinGuardar = (negocio?.plantillaPedidoWa || PLANTILLA_CANONICA_ES_MX) !== plantilla;

  const guardarPlantilla = async () => {
    setGuardando(true);
    /*
      Guardar el texto canónico como `null` y no como copia: así, si el default mejora en un sprint
      futuro, quien nunca la editó se beneficia solo. Quien la escribió a mano conserva la suya.
    */
    const r = await window.api.negocio.actualizar({
      plantillaPedidoWa: plantilla.trim() === PLANTILLA_CANONICA_ES_MX.trim() ? null : plantilla,
    });
    setGuardando(false);
    if (r.ok) {
      await cargarNegocio();
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    } else {
      await window.alertar('No se pudo guardar la plantilla. Inténtalo otra vez.');
    }
  };

  /** Lo que se manda al chat del cliente es el TEXTO, no el link: se pega y se rellena ahí mismo. */
  const copiarTexto = async () => {
    try {
      await navigator.clipboard.writeText(plantilla);
      setCopiadoTexto(true);
      setTimeout(() => setCopiadoTexto(false), 2000);
    } catch {
      window.alertar('No se pudo copiar. Selecciona el texto y cópialo a mano.');
    }
  };

  const telDigits = telefono.replace(/\D/g, '');
  const telValido = telDigits.length >= 10;
  const link = useMemo(
    () => (telValido ? construirLinkWaMe(telefono, plantilla) : ''),
    [telefono, plantilla, telValido],
  );

  const onCopiar = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Fallback: seleccionar el textarea.
      window.alertar('No se pudo copiar automáticamente. Selecciona y copia el link manualmente.');
    }
  };

  const onProbar = () => {
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const restaurarPlantilla = () => {
    setPlantilla(PLANTILLA_CANONICA_ES_MX);
  };

  return (
    <div className="max-w-3xl space-y-5">
      <header className="flex items-start gap-3">
        <div className="rounded-full bg-green-100 p-2 shrink-0">
          <MessageCircle className="h-5 w-5 text-green-700" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Plantilla de pedido por WhatsApp</h2>
          <p className="text-sm text-slate-600 mt-1">
            Genera un link que abre WhatsApp con el formato listo. Tus clientes
            solo rellenan los datos y te lo mandan. Cuando lo pegues en el sistema,
            se detecta automáticamente lo que dice.
          </p>
        </div>
      </header>

      {/* Instrucciones */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 flex items-start gap-2 text-sm text-blue-900">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">¿Cómo lo uso?</p>
          <ol className="list-decimal list-inside mt-1 space-y-0.5 text-blue-800">
            <li>Copia el link de abajo.</li>
            <li>Pégalo en tu Instagram bio, Facebook, estados de WhatsApp, o cuando un cliente te escriba para pedir.</li>
            <li>Cuando hacen clic, su WhatsApp abre con el formato listo para rellenar.</li>
          </ol>
        </div>
      </div>

      {/* Teléfono del negocio */}
      <div>
        <label className="label" htmlFor="tel-negocio">
          Tu número de WhatsApp (donde llegan los pedidos)
        </label>
        <input
          id="tel-negocio"
          className="input"
          value={telefono}
          onChange={(e) => setTelefono(e.target.value)}
          placeholder="9931234567"
          inputMode="tel"
        />
        {!telValido && telefono && (
          <p className="text-xs text-amber-700 mt-1">
            Debe tener al menos 10 dígitos. Si vives en México, no agregues el +52
            — lo añadimos automáticamente.
          </p>
        )}
      </div>

      {/* Plantilla */}
      <div>
        <div className="flex items-center justify-between">
          <label className="label" htmlFor="plantilla-wa">Plantilla del mensaje</label>
          <button
            type="button"
            onClick={restaurarPlantilla}
            className="text-xs text-primary hover:underline"
          >
            Restaurar plantilla original
          </button>
        </div>
        <textarea
          id="plantilla-wa"
          className="input font-mono text-sm"
          rows={14}
          value={plantilla}
          onChange={(e) => setPlantilla(e.target.value)}
        />
        <p className="text-xs text-slate-500 mt-1">
          Edítala si quieres. Mantén las etiquetas (📋 Pedido, 📞 Teléfono,
          📍 Dirección) — el sistema las usa para detectar los datos cuando
          pegues el mensaje.
        </p>

        {/*
          Sprint 98 — Guardar y copiar, aquí mismo.

          El botón de guardar es nuevo porque hasta ahora no había nada que guardar: la edición se
          perdía al salir. Y el de copiar el texto es el que hace útil la plantilla en el día a día
          — es lo que se pega en el chat del cliente. (El link de abajo es para bio y estados: abre
          WhatsApp con el formato puesto, que es otro uso.)
        */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={guardarPlantilla}
            disabled={guardando || !sinGuardar}
            className="btn-primary"
          >
            {guardado ? <Check className="h-4 w-4" /> : null}
            {guardando ? 'Guardando…' : guardado ? 'Guardada' : 'Guardar plantilla'}
          </button>
          <button type="button" onClick={copiarTexto} className="btn-ghost border border-slate-300">
            {copiadoTexto ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copiadoTexto ? '¡Copiada!' : 'Copiar el texto para mandarlo'}
          </button>
          {sinGuardar && !guardando && (
            <span className="text-xs text-amber-700">Tienes cambios sin guardar.</span>
          )}
        </div>
      </div>

      {/* Link generado */}
      <div className="card border-2 border-green-200 bg-green-50/50">
        <h3 className="font-semibold text-slate-800 mb-2">Tu link de WhatsApp</h3>
        {telValido ? (
          <>
            <textarea
              readOnly
              className="input font-mono text-xs"
              rows={3}
              value={link}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
            />
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={onCopiar} className="btn-primary">
                {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copiado ? '¡Copiado!' : 'Copiar link'}
              </button>
              <button onClick={onProbar} className="btn-ghost">
                <ExternalLink className="h-4 w-4" />
                Probar (abre WhatsApp)
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500">
            Escribe tu número de WhatsApp arriba para generar el link.
          </p>
        )}
      </div>
    </div>
  );
}
